import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { DialogueIcon } from '@treams/treams-icons';
import { Link } from '@treams/treams-routes';
import { Empty, Heading, Paragraph, Skeleton, Subheading } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

import FeedbackResultAnswer from './FeedbackResultAnswer';
import GoalCard from 'goals/GoalCard';

const Container = styled(Box)`
	page-break-inside: avoid;
`;

const Others = styled(Link)`
	color: ${(props) => props.theme.colors.primary};

	&:hover {
		text-decoration: underline;

		&:active {
			color: black;
		}
	}
`;

export default function FeedbackResultQuestion(props: {
	request: graphql.FeedbackRequest;
	question: graphql.FeedbackRequestQuestion;
	response?: graphql.FeedbackResponse;
	responses: graphql.FeedbackResponse[];
	small?: boolean;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const answers = (props.response ? [props.response] : props.responses)
		.filter((response) => response.status === graphql.FeedbackResponseStatus.Shared)
		.map((response) => {
			const answer = response.answers?.find(({ questionID }) => questionID === props.question.id);
			return {
				response,
				answer,
			};
		})
		.filter(({ answer }) => !!answer);

	const shared = props.responses
		.filter((response) => response.id !== props.response?.id)
		.filter((response) => response.status === graphql.FeedbackResponseStatus.Shared).length;

	return (
		<Container>
			{props.small ? (
				<Paragraph>{props.question.text}</Paragraph>
			) : (
				<Heading>{props.question.text}</Heading>
			)}
			{props.question.goal && (
				<Box mt={2}>
					{/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
					<GoalCard small goal={props.question.goal as any} />
				</Box>
			)}
			{props.question.goalRevision && (
				<Box mt={2}>
					<GoalCard small goal={props.question.goalRevision} modal />
				</Box>
			)}
			{props.question.competency && (
				<Subheading mt={2}>{localize(props.question.competency.title).text}</Subheading>
			)}
			<Box mt={props.small ? 2 : 3}>
				{answers.length > 0 ? (
					answers.map(({ response, answer }) => (
						<Box key={response.id} mt={props.small ? 2 : 3}>
							<FeedbackResultAnswer
								response={response}
								recipient={response.recipient}
								question={props.question}
								answer={answer!}
							/>
						</Box>
					))
				) : (
					<Skeleton.Empty
						contents={
							<Empty
								icon={<DialogueIcon />}
								title={t('feedback.results.question.empty.title')}
								small
							/>
						}
					>
						<Box mt={3}>
							<FeedbackResultAnswer.Skeleton />
						</Box>
					</Skeleton.Empty>
				)}
			</Box>
			{shared > 0 && props.response && (
				<Box mt={2} ml={40}>
					<Others to={`/feedback/${props.request.id}`}>
						{t('feedback.results.question.more', { count: shared })}
					</Others>
				</Box>
			)}
		</Container>
	);
}

function FeedbackResultQuestionSkeleton() {
	return (
		<Container>
			<Heading.Skeleton />
			<Skeleton.Repeat count={3}>
				<Box mt={3}>
					<FeedbackResultAnswer.Skeleton />
				</Box>
			</Skeleton.Repeat>
		</Container>
	);
}

FeedbackResultQuestion.Skeleton = FeedbackResultQuestionSkeleton;
